import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

import { BreadcrumbHeader, DataTable, EmptyState } from '../../components/shared';
import { warehouses, customerAssignedLocations, stockLevels, parts } from '../../services/dataClient';
import { useAuth } from '../../auth';
import '../../styles/design-tokens.css';import ContentCard from "../../components/shared/ContentCard";

export default function PortalLocationsPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Only assigned On-Site locations for this customer
  const assignedNames = useMemo(
    () => customerAssignedLocations
    .filter((a) => a.customerName === currentUser.customerName)
    .map((a) => a.locationName),
    [currentUser.customerName]
  );

  const myLocations = useMemo(
    () => warehouses.filter((w) => assignedNames.includes(w.name) && w.type === 'On-Site'),
    [assignedNames]
  );

  // Aggregate stock stats per location
  const locationStats = useMemo(() => {
    const map = {};
    myLocations.forEach((w) => {
      const rows = stockLevels.filter((s) => s.locationName === w.name);
      const totalOnHand = rows.reduce((sum, s) => sum + (s.quantityOnHand ?? 0), 0);
      const totalThreshold = rows.reduce((sum, s) => {
        const part = parts.find((p) => p.partNumber === s.partNumber);
        return sum + (part?.reorderThreshold ?? 0);
      }, 0);
      map[w.name] = { totalOnHand, totalThreshold, partCount: rows.length };
    });
    return map;
  }, [myLocations]);

  const displayLocations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return myLocations;
    return myLocations.filter((w) =>
    w.name.toLowerCase().includes(q) ||
    w.city.toLowerCase().includes(q) ||
    w.state.toLowerCase().includes(q)
    );
  }, [myLocations, search]);

  const columns = [
  { field: 'code', headerName: 'Code', sortable: true },
  {
    field: 'name',
    headerName: 'Location',
    sortable: true,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
          {row.name}
        </Typography>

  },
  {
    field: 'city',
    headerName: 'City / State',
    sortable: true,
    renderCell: (row) => `${row.city}, ${row.state}`
  },
  {
    field: '_onHand',
    headerName: 'On Hand',
    sortable: false,
    renderCell: (row) => {
      const stats = locationStats[row.name];
      const low = stats && stats.totalOnHand < stats.totalThreshold;
      return (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: low ? '#E65100' : 'var(--grey-800)' }}>
            {stats?.totalOnHand ?? 0}
          </Typography>);

    }
  },
  {
    field: '_threshold',
    headerName: 'Threshold',
    sortable: false,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)' }}>
          {locationStats[row.name]?.totalThreshold ?? 0}
        </Typography>

  },
  {
    field: '_parts',
    headerName: 'Parts',
    sortable: false,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)' }}>
          {locationStats[row.name]?.partCount ?? 0}
        </Typography>

  },
  { field: 'contactName', headerName: 'Contact', sortable: false }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BreadcrumbHeader
        title="Stock Locations"
        crumbs={[{ label: 'Portal' }, { label: 'Stock Locations' }]} />

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by name, city, or state…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: 'var(--grey-400)' }} /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& fieldset': { borderColor: 'var(--grey-200)' },
              '&:hover fieldset': { borderColor: 'var(--grey-400)' },
              '&.Mui-focused fieldset': { borderColor: '#009688' }
            }
          }} />

        <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
          {myLocations.length === 0 ?
          <Box sx={{ p: 3 }}>
              <EmptyState title="No locations assigned" description="Your account has no on-site stock locations assigned yet." />
            </Box> :
          displayLocations.length === 0 ?
          <Box sx={{ p: 3 }}>
              <EmptyState title="No locations found" description="No locations match your search." />
            </Box> :

          <DataTable
            columns={columns}
            data={displayLocations}
            onRowClick={(row) => navigate(`/portal/inventory?location=${encodeURIComponent(row.name)}`)} />

          }
        </Box>
      </ContentCard>
    </Box>);

}